<script setup lang="ts">
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { UiText, UiTabs, UiTabsItem } from "@shopexperts/shared";
import { useCurrentExpertOrgId, useExpertWallet } from "@/app/composables/wallet";
import ExpertWalletSummaryCard from "@/components/expert/wallet/ExpertWalletSummaryCard.vue";
import ExpertWalletTransactionList from "@/components/expert/wallet/ExpertWalletTransactionList.vue";
import ExpertPayoutRequestList from "@/components/expert/wallet/ExpertPayoutRequestList.vue";

const { orgId } = useCurrentExpertOrgId();
const { allowPayouts, availableBalanceCents, currency } = useExpertWallet({ actingOrgId: orgId });

const route = useRoute();
const router = useRouter();

type TabValue = "transactions" | "payouts";

function readInitialTab(): TabValue {
  return route.query.tab === "payouts" ? "payouts" : "transactions";
}

const activeTab = ref<TabValue>(readInitialTab());

watch(activeTab, (next) => {
  const current = typeof route.query.tab === "string" ? route.query.tab : "transactions";
  if (current === next) return;
  const nextQuery: Record<string, string> = { ...route.query as Record<string, string>, tab: next };
  if (next === "transactions") delete nextQuery.tab;
  router.replace({ query: nextQuery });
});

watch(() => route.query.tab, (tab) => {
  const next: TabValue = tab === "payouts" ? "payouts" : "transactions";
  if (next !== activeTab.value) activeTab.value = next;
});
</script>

<template>
  <main class="font-archivo flex-1 overflow-y-auto bg-secondary p-4 sm:p-6">
    <div class="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <header class="flex flex-col gap-1">
        <UiText as="h1" variant="h3" text="Wallet" class="font-semibold text-se-dark" />
        <UiText
          as="p"
          variant="p"
          text="Track your balance, expert level, and every transaction in one place."
          class="text-se-gray-600"
        />
      </header>

      <ExpertWalletSummaryCard :acting-org-id="orgId" />

      <UiTabs v-if="allowPayouts" v-model="activeTab" default-value="transactions">
        <UiTabsItem value="transactions" label="Transactions">
          <ExpertWalletTransactionList :acting-org-id="orgId" class="pt-4" />
        </UiTabsItem>
        <UiTabsItem value="payouts" label="Payouts">
          <ExpertPayoutRequestList
            :acting-org-id="orgId"
            :available-balance-cents="availableBalanceCents"
            :currency="currency"
            class="pt-4"
          />
        </UiTabsItem>
      </UiTabs>

      <ExpertWalletTransactionList v-else :acting-org-id="orgId" />
    </div>
  </main>
</template>
